/**
 *  @class
 *  @function SearchForm
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.button = document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      this.predictiveSearchResults = this.is_drawer ? this.querySelector('.side-panel-content--has-tabs') : this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.promotionTemplate = this.querySelector('.thb-predictive-search--promotion-template');
      this.promotionHydrated = false;
      this.cache = new Map();
      this.cacheLimit = 50;
      this.lastSearchTerm = '';
      this.searchRequestId = 0;
      this.useStorefrontSearch = document.documentElement.lang.toLowerCase().startsWith('zh');

      this.setupEventListeners();
    }

    hydratePromotion() {
      if (!this.promotionTemplate) {
        return;
      }
      this.promotionTemplate.parentNode.insertBefore(
        this.promotionTemplate.content,
        this.promotionTemplate
      );
      this.promotionTemplate.remove();
      this.promotionTemplate = null;
      this.promotionHydrated = true;
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
      this.input.addEventListener('focus', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.button.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          this.hydratePromotion();
          if (this.is_drawer) {
            OverlayManager.open(this.drawer);
            setTimeout(() => {
              this.input.focus({
                preventScroll: true
              });
            }, 100);
          } else {
            document.body.classList.add('open-cc');
          }
          return false;
        });
      });

      if (!this.is_drawer) {
        this.onCcClickBound = this.close.bind(this);
        this.onKeyUpBound = (e) => {
          if (e.code && e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        };
        this.cc?.addEventListener('click', this.onCcClickBound);
        document.addEventListener('keyup', this.onKeyUpBound);
      }
    }

    disconnectedCallback() {
      if (this.onKeyUpBound) {
        document.removeEventListener('keyup', this.onKeyUpBound);
      }
      if (this.onCcClickBound && this.cc) {
        this.cc.removeEventListener('click', this.onCcClickBound);
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.lastSearchTerm = '';
        this.showDefaultContent();
        return;
      }
      if (!this.is_drawer) {
        this.open();
      }
      const hasCurrentResults = this.predictiveSearchResults.innerHTML.trim().length > 0;
      const hasCurrentRequest = this.predictiveSearchResults.classList.contains('loading');
      if (searchTerm === this.lastSearchTerm && (hasCurrentResults || hasCurrentRequest)) {
        return;
      }
      this.lastSearchTerm = searchTerm;
      this.showSearchResults();
      this.getSearchResults(searchTerm);
    }

    showDefaultContent() {
      this.searchRequestId++;
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.remove('active', 'loading');
        this.predictiveSearchResults.setAttribute('aria-busy', 'false');
        this.predictiveSearchResults.innerHTML = '';
      }
      if (this.defaultTab) {
        this.defaultTab.style.display = '';
      }
      if (!this.is_drawer) {
        this.close();
      }
    }

    showSearchResults() {
      if (this.defaultTab) {
        this.defaultTab.style.display = 'none';
      }
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('active');
      }
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    async getSearchResults(searchTerm) {
      const requestId = ++this.searchRequestId;
      const cached = this.cache.get(searchTerm);
      if (cached) {
        this.renderSearchResults(cached);
        this.predictiveSearchResults.classList.remove('loading');
        this.predictiveSearchResults.setAttribute('aria-busy', 'false');
        return;
      }

      this.predictiveSearchResults.innerHTML = '';
      this.predictiveSearchResults.classList.add('loading');
      this.predictiveSearchResults.setAttribute('aria-busy', 'true');
      try {
        const resultsMarkup = await this.fetchSearchMarkup(searchTerm, this.useStorefrontSearch);
        if (requestId !== this.searchRequestId || searchTerm !== this.getQuery()) {
          return;
        }
        if (this.cache.size >= this.cacheLimit) {
          this.cache.delete(this.cache.keys().next().value);
        }
        this.cache.set(searchTerm, resultsMarkup);
        this.renderSearchResults(resultsMarkup);
      } catch (error) {
        if (requestId === this.searchRequestId && searchTerm === this.getQuery()) {
          this.renderSearchFallback(searchTerm);
        }
      } finally {
        if (requestId === this.searchRequestId) {
          this.predictiveSearchResults.classList.remove('loading');
          this.predictiveSearchResults.setAttribute('aria-busy', 'false');
        }
      }
    }

    async fetchSearchMarkup(searchTerm, storefrontSearch) {
      let url;
      if (storefrontSearch) {
        url = new URL(this.form.getAttribute('action'), window.location.origin);
        url.searchParams.set('q', searchTerm);
        url.searchParams.set('type', 'product,article,page');
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('section_id', 'predictive-search');
      } else {
        url = new URL(theme.routes.predictive_search_url, window.location.origin);
        url.searchParams.set('q', searchTerm);
        url.searchParams.set('resources[type]', 'product,article,query,page');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku');
        url.searchParams.set('section_id', 'predictive-search');
      }
      const response = await fetch(url.toString());
      if (response.status === 417 && !storefrontSearch) {
        this.useStorefrontSearch = true;
        return this.fetchSearchMarkup(searchTerm, true);
      }
      if (!response.ok) {
        throw new Error(response.status);
      }
      const text = await response.text();
      const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
      if (!section || !section.innerHTML.trim()) {
        throw new Error('Missing search results');
      }
      return section.innerHTML;
    }

    renderSearchFallback(searchTerm) {
      const chinese = document.documentElement.lang.toLowerCase().startsWith('zh');
      const message = document.createElement('p');
      message.setAttribute('role', 'status');
      message.textContent = chinese ? '暫時未能載入即時結果，請查看完整搜尋結果。' : 'Live results are temporarily unavailable. View all search results below.';
      const footer = document.createElement('div');
      footer.className = 'side-panel-content--search-footer';
      const link = document.createElement('a');
      const url = new URL(this.form.getAttribute('action'), window.location.origin);
      url.searchParams.set('q', searchTerm);
      link.setAttribute('href', url.pathname + url.search);
      link.className = 'button full';
      link.textContent = chinese ? '查看所有搜尋結果' : 'View all results';
      footer.appendChild(link);
      this.predictiveSearchResults.replaceChildren(message, footer);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      const submitBtn = this.predictiveSearchResults.querySelector('#search-results-submit');
      if (submitBtn) {
        if (!this.is_drawer) {
          submitBtn.classList.remove('button');
        }
        submitBtn.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }

    close() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.setAttribute('inert', '');
      }
      document.body.classList.remove('open-search');
    }
    open() {
      document.body.classList.add('open-search');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.removeAttribute('inert');
      }
    }
  }
  customElements.define('search-form', SearchForm);
}
